import { RelayServerTransport } from '~/modules/remote-mic/network/server/transport/relay-server-transport';
import { getRemoteMicOrigin, normaliseOrigin } from '~/modules/utils/remote-mic-origin';

/** Where the machine serving the game offers a relay, if it offers one - see `vite-plugin-lan-relay`. */
export const LAN_RELAY_PATH = '/lan-relay';

/** A room id the relay accepts and closes again, so asking whether it is there costs one socket. */
const PROBE_ROOM = '__probe';

/** How long that question is given to answer before the game gets on with the relays it knows. */
const PROBE_TIMEOUT_MS = 2_000;

/**
 * The origin to host a room on, or empty when this machine is not one a phone could reach. The room
 * link already answers that question - it points at this origin exactly when a phone can open it,
 * and at the public site when it cannot - so the two can never disagree.
 */
const reachableOrigin = () => {
  const origin = normaliseOrigin(global.location?.origin ?? '');

  return origin && getRemoteMicOrigin() === origin ? origin : '';
};

export const lanRelayUrl = (roomId: string) => `${reachableOrigin().replace(/^http/, 'ws')}${LAN_RELAY_PATH}/${roomId}`;

/**
 * Whether this origin hosts a relay of its own. Asked rather than assumed: the same code runs on a
 * deployed site, which a phone can reach perfectly well and which has no relay behind it. Asked
 * over a socket rather than over HTTP, because in a preview the app's own Worker answers every HTTP
 * path - and because opening a socket is exactly the thing being asked about.
 */
export const detectLanRelay = (): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    if (!reachableOrigin() || typeof WebSocket === 'undefined') {
      resolve(false);

      return;
    }

    let settled = false;
    const answer = (hosted: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      try {
        socket.close();
      } catch {
        // Already gone, which is an answer in itself.
      }
      resolve(hosted);
    };

    const timeout = setTimeout(() => answer(false), PROBE_TIMEOUT_MS);
    const socket = new WebSocket(lanRelayUrl(PROBE_ROOM));
    socket.onopen = () => answer(true);
    socket.onerror = () => answer(false);
    // A close without an open is a socket nothing was listening for.
    socket.onclose = () => answer(false);
  });

export class LanRelayServerTransport extends RelayServerTransport {
  public readonly name = 'LanRelay';

  protected getUrl = (roomId: string) => lanRelayUrl(roomId);
}
